<template>
  <div>
    <!-- Readiness Summary Alert - Include in parent component -->
    <div v-if="show" class="readiness-alert-container">

      <!-- Scanning State (Active) -->
      <div v-if="isScanning && !isPaused" class="card-header bg-info bg-soft border-bottom py-3">
        <div class="d-flex justify-content-between align-items-center flex-wrap">
          <div class="d-flex align-items-center mb-2 mb-md-0">
            <div class="spinner-border text-info me-3" role="status">
              <span class="visually-hidden">Scanning...</span>
            </div>
            <div>
              <h5 class="mb-1 text-info fw-bold">
                <i class="bx bx-search-alt me-1"></i> Scanning Businesses...
              </h5>
              <p class="mb-0 text-dark">
                Checked <strong>{{ numberFormat(scanProgress.checked) }}</strong> of <strong>{{ numberFormat(scanProgress.total) }}</strong> businesses
                <span class="ms-2 badge bg-success">Ready: {{ numberFormat(scanProgress.ready) }}</span>
                <span class="badge bg-danger">Not Ready: {{ numberFormat(scanProgress.notReady) }}</span>
              </p>
            </div>
          </div>
          <div class="d-flex align-items-center">
            <div class="text-end me-3">
              <div class="mb-1">
                <span class="badge bg-info fs-6">
                  <i class="bx bx-time me-1"></i>
                  {{ formatTime(scanProgress.elapsedTime) }}
                </span>
                <span v-if="scanProgress.estimatedRemaining > 0" class="badge bg-secondary fs-6 ms-1">
                  ~{{ formatTime(scanProgress.estimatedRemaining) }} left
                </span>
              </div>
              <div class="progress" style="width: 150px; height: 6px;">
                <div
                    class="progress-bar progress-bar-striped progress-bar-animated bg-info"
                    role="progressbar"
                    :style="{ width: scanProgressPercent + '%' }"
                ></div>
              </div>
            </div>
            <div class="btn-group">
              <button
                  @click="pauseScan()"
                  class="btn btn-warning btn-sm"
                  title="Pause scanning"
              >
                <i class="bx bx-pause"></i> Pause
              </button>
              <button
                  @click="cancelScan()"
                  class="btn btn-danger btn-sm"
                  title="Cancel scanning"
              >
                <i class="bx bx-x"></i> Cancel
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Scanning Paused State -->
      <div v-else-if="isPaused" class="card-header bg-warning bg-soft border-bottom py-3">
        <div class="d-flex justify-content-between align-items-center flex-wrap">
          <div class="d-flex align-items-center mb-2 mb-md-0">
            <i class="bx bx-pause-circle text-warning font-size-24 me-3"></i>
            <div>
              <h5 class="mb-1 text-warning fw-bold">
                <i class="bx bx-pause me-1"></i> Scanning Paused
              </h5>
              <p class="mb-0 text-dark">
                Checked <strong>{{ numberFormat(scanProgress.checked) }}</strong> of <strong>{{ numberFormat(scanProgress.total) }}</strong> businesses
                <span class="ms-2 badge bg-success">Ready: {{ numberFormat(scanProgress.ready) }}</span>
                <span class="badge bg-danger">Not Ready: {{ numberFormat(scanProgress.notReady) }}</span>
                <span class="badge bg-info ms-1">{{ scanProgressPercent }}% complete</span>
              </p>
            </div>
          </div>
          <div class="btn-group">
            <button
                @click="resumeScan()"
                class="btn btn-success btn-sm"
                title="Resume scanning"
            >
              <i class="bx bx-play"></i> Resume
            </button>
            <button
                @click="cancelScan()"
                class="btn btn-danger btn-sm"
                title="Cancel scanning"
            >
              <i class="bx bx-x"></i> Cancel
            </button>
          </div>
        </div>
      </div>

      <!-- Not Ready Alert (Scan Complete) -->
      <div v-else-if="scanCompleted && summary.notReady > 0" class="card-header bg-danger bg-soft border-bottom py-3">
        <div class="d-flex justify-content-between align-items-center flex-wrap">
          <div class="d-flex align-items-center mb-2 mb-md-0">
            <i class="bx bx-error-circle text-danger font-size-24 me-3"></i>
            <div>
              <h5 class="mb-1 text-danger fw-bold">
                {{ numberFormat(summary.notReady) }} Business(es) Need Fee Setup
              </h5>
              <p class="mb-0 text-dark">
                These businesses don't have fee records and cannot be invoiced until updated.
                <span class="badge bg-success ms-2">Ready: {{ numberFormat(summary.ready) }}</span>
                <span class="badge bg-danger ms-1">Not Ready: {{ numberFormat(summary.notReady) }}</span>
                <span class="badge bg-secondary ms-1">Total: {{ numberFormat(summary.total) }}</span>
              </p>
            </div>
          </div>
          <div>
            <button
                @click="openModal()"
                class="btn btn-danger me-2"
            >
              <i class="bx bx-list-ul me-1"></i> View & Update List
            </button>
            <button
                @click="startFullScan()"
                class="btn btn-outline-info"
                title="Re-scan all businesses"
            >
              <i class="bx bx-refresh me-1"></i> Re-Scan
            </button>
          </div>
        </div>
      </div>

      <!-- All Ready Alert (Scan Complete) -->
      <div v-else-if="scanCompleted && summary.total > 0 && summary.notReady === 0" class="card-header bg-success bg-soft border-bottom py-2">
        <div class="d-flex align-items-center justify-content-between">
          <div class="d-flex align-items-center">
            <i class="bx bx-check-circle text-success font-size-20 me-2"></i>
            <span class="text-success fw-semibold">
              All {{ numberFormat(summary.ready) }} businesses are ready for invoicing
            </span>
          </div>
          <button
              @click="startFullScan()"
              class="btn btn-sm btn-outline-success"
              title="Re-scan all businesses"
          >
            <i class="bx bx-refresh me-1"></i> Re-Scan
          </button>
        </div>
      </div>

      <!-- Initial State - Not Yet Scanned -->
      <div v-else class="card-header bg-light border-bottom py-3">
        <div class="d-flex justify-content-between align-items-center flex-wrap">
          <div class="d-flex align-items-center mb-2 mb-md-0">
            <i class="bx bx-info-circle text-primary font-size-24 me-3"></i>
            <div>
              <h5 class="mb-1 text-primary fw-bold">
                Business Readiness Check Required
              </h5>
              <p class="mb-0 text-muted">
                Scan {{ numberFormat(totalPendingRecords) }} pending businesses to check which ones are ready for invoicing.
                <br>
                <small class="text-muted">This will check if each business has fee records in the system.</small>
              </p>
            </div>
          </div>
          <button
              @click="startFullScan()"
              class="btn btn-primary"
              :disabled="totalPendingRecords === 0"
          >
            <i class="bx bx-search-alt me-1"></i> Start Scanning
          </button>
        </div>
      </div>
    </div>

    <!-- Business Readiness Modal -->
    <div
        class="modal fade"
        id="businessReadinessModal"
        tabindex="-1"
        role="dialog"
        aria-labelledby="businessReadinessModalLabel"
        aria-hidden="true"
        ref="readinessModal"
    >
      <div class="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
        <div class="modal-content">

          <!-- Modal Header -->
          <div class="modal-header bg-danger text-white">
            <div class="d-flex align-items-center">
              <i class="bx bx-error-circle font-size-24 me-2"></i>
              <div>
                <h5 class="modal-title mb-0" id="businessReadinessModalLabel">
                  Businesses Needing Fee Setup
                </h5>
                <small class="opacity-75">
                  {{ numberFormat(businessesNeedingUpdate.length) }} business(es) require update before invoicing
                </small>
              </div>
            </div>
            <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>

          <!-- Modal Body -->
          <div class="modal-body p-0">

            <!-- Info Alert -->
            <div class="alert alert-warning rounded-0 mb-0 border-start-0 border-end-0">
              <div class="d-flex align-items-start">
                <i class="bx bx-info-circle font-size-20 me-2 mt-1"></i>
                <div>
                  <strong>Why are these businesses not ready?</strong>
                  <p class="mb-0 mt-1">
                    These businesses don't have their fee structure configured for the upcoming billing period.
                    Click <strong>"Update"</strong> to open the verification form and set up their fees and charges.
                  </p>
                </div>
              </div>
            </div>

            <!-- Search and Pagination -->
            <div class="p-3 border-bottom bg-light">
              <div class="row align-items-center">
                <div class="col-md-5">
                  <div class="input-group">
                    <span class="input-group-text bg-white">
                      <i class="bx bx-search"></i>
                    </span>
                    <input
                        type="text"
                        class="form-control"
                        placeholder="Search by business name, ID, or owner..."
                        v-model="searchQuery"
                    >
                    <button
                        v-if="searchQuery"
                        class="btn btn-outline-secondary"
                        type="button"
                        @click="searchQuery = ''"
                    >
                      <i class="bx bx-x"></i>
                    </button>
                  </div>
                </div>
                <div class="col-md-3">
                  <select class="form-select" v-model="modalPageSize">
                    <option value="10">10 per page</option>
                    <option value="25">25 per page</option>
                    <option value="50">50 per page</option>
                    <option value="100">100 per page</option>
                  </select>
                </div>
                <div class="col-md-4 text-md-end mt-2 mt-md-0">
                  <span class="text-muted">
                    Showing {{ paginatedBusinesses.length }} of {{ filteredBusinesses.length }} businesses
                  </span>
                </div>
              </div>
            </div>

            <!-- Table -->
            <div class="table-responsive" style="max-height: 400px; overflow-y: auto;">
              <table class="table table-hover table-striped align-middle mb-0">
                <thead class="bg-light sticky-top">
                <tr>
                  <th style="width: 50px;">#</th>
                  <th>Business ID</th>
                  <th>Business Name</th>
                  <th>Owner</th>
                  <th>Phone</th>
                  <th>Location</th>
                  <th>Fee ID</th>
                  <th style="width: 120px;" class="text-center">Action</th>
                </tr>
                </thead>
                <tbody>
                <tr v-if="paginatedBusinesses.length === 0">
                  <td colspan="8" class="text-center py-4">
                    <div class="text-muted">
                      <i class="bx bx-search-alt font-size-24 d-block mb-2"></i>
                      <span v-if="searchQuery">No businesses match your search</span>
                      <span v-else>No businesses needing update</span>
                    </div>
                  </td>
                </tr>
                <tr v-for="(business, idx) in paginatedBusinesses" :key="business.businessID">
                  <td class="text-muted">{{ (modalCurrentPage - 1) * modalPageSize + idx + 1 }}</td>
                  <td>
                    <code class="bg-light px-2 py-1 rounded">{{ business.businessID }}</code>
                  </td>
                  <td>
                    <div class="fw-semibold">{{ business.businessName }}</div>
                    <small class="text-muted" v-if="business.businessSubCategory">
                      {{ business.businessSubCategory }}
                    </small>
                  </td>
                  <td>{{ business.fullNames || business.owner || '-' }}</td>
                  <td>
                      <span v-if="business.businessPhone || business.ownerPhone">
                        {{ business.businessPhone || business.ownerPhone }}
                      </span>
                    <span v-else class="text-muted">-</span>
                  </td>
                  <td>
                    <div class="small">
                      <i class="bx bx-map text-primary"></i>
                      {{ business.subCountyName || '-' }}
                    </div>
                    <div class="small text-muted">
                      {{ business.wardName || '-' }}
                    </div>
                  </td>
                  <td>
                      <span v-if="business.feeID" class="badge bg-info">
                        {{ business.feeID }}
                      </span>
                    <span v-else class="badge bg-danger">
                        <i class="bx bx-x"></i> None
                      </span>
                  </td>
                  <td class="text-center">
                    <button
                        @click="initiateUpdate(business, idx)"
                        class="btn btn-sm btn-success d-flex align-items-center justify-content-center w-100"
                        :disabled="updatingIndex === idx"
                    >
                      <span v-if="updatingIndex === idx" class="spinner-border spinner-border-sm me-1"></span>
                      <i v-else class="bx bx-edit me-1"></i>
                      Update
                    </button>
                  </td>
                </tr>
                </tbody>
              </table>
            </div>

            <!-- Pagination -->
            <div class="p-3 border-top bg-light" v-if="filteredBusinesses.length > modalPageSize">
              <nav class="d-flex justify-content-between align-items-center">
                <div>
                  Page {{ modalCurrentPage }} of {{ modalTotalPages }}
                </div>
                <ul class="pagination pagination-sm mb-0">
                  <li class="page-item" :class="{ disabled: modalCurrentPage === 1 }">
                    <a class="page-link" href="#" @click.prevent="modalCurrentPage = 1">
                      <i class="bx bx-chevrons-left"></i>
                    </a>
                  </li>
                  <li class="page-item" :class="{ disabled: modalCurrentPage === 1 }">
                    <a class="page-link" href="#" @click.prevent="modalCurrentPage--">
                      <i class="bx bx-chevron-left"></i>
                    </a>
                  </li>
                  <li class="page-item" :class="{ disabled: modalCurrentPage === modalTotalPages }">
                    <a class="page-link" href="#" @click.prevent="modalCurrentPage++">
                      <i class="bx bx-chevron-right"></i>
                    </a>
                  </li>
                  <li class="page-item" :class="{ disabled: modalCurrentPage === modalTotalPages }">
                    <a class="page-link" href="#" @click.prevent="modalCurrentPage = modalTotalPages">
                      <i class="bx bx-chevrons-right"></i>
                    </a>
                  </li>
                </ul>
              </nav>
            </div>

            <!-- Loading Overlay -->
            <div v-if="loadingBusinesses" class="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-white bg-opacity-75" style="z-index: 10;">
              <div class="text-center">
                <div class="spinner-border text-primary mb-2"></div>
                <div>Loading businesses...</div>
              </div>
            </div>
          </div>

          <!-- Modal Footer -->
          <div class="modal-footer bg-light">
            <div class="d-flex justify-content-between align-items-center w-100">
              <div class="text-muted small">
                <i class="bx bx-info-circle me-1"></i>
                Click "Update" to open the verification form for each business
              </div>
              <div>
                <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
                  <i class="bx bx-x me-1"></i> Close
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
/* eslint-disable */
import { execute, trade } from "@/api";

export default {
  name: "BusinessReadinessModal",

  props: {
    // Array of entries from parent (to check readiness)
    entries: {
      type: Array,
      default: () => []
    },
    // Fiscal year for checking
    fiscalYear: {
      type: [String, Number],
      default: ''
    },
    // Whether to show the alert bar
    show: {
      type: Boolean,
      default: true
    },
    // SubCounty filter from parent
    subCountyID: {
      type: String,
      default: ''
    },
    subCountyName: {
      type: String,
      default: ''
    },
    // Ward filter from parent
    wardID: {
      type: String,
      default: ''
    },
    wardName: {
      type: String,
      default: ''
    },
    // Zone filter
    zone: {
      type: String,
      default: ''
    },
    // Total pending records from parent
    totalPendingRecords: {
      type: Number,
      default: 0
    }
  },

  data() {
    return {
      loading: false,
      loadingBusinesses: false,
      updatingIndex: null,
      searchQuery: '',
      modalInstance: null,

      // Scanning state
      isScanning: false,
      isPaused: false,
      scanCompleted: false,
      scanTimer: null,
      scanProgress: {
        total: 0,
        checked: 0,
        ready: 0,
        notReady: 0,
        currentPage: 1,
        elapsedTime: 0,
        estimatedRemaining: 0,
        startTime: null,
        pausedPage: 1 // Track where we paused
      },

      // Summary counts
      summary: {
        total: 0,
        ready: 0,
        notReady: 0,
        fiscalYear: ''
      },

      // List of businesses needing update
      businessesNeedingUpdate: [],

      // Modal pagination
      modalCurrentPage: 1,
      modalPageSize: 25,

      // Track the last business that was sent for update
      lastUpdatedBusinessID: null
    };
  },

  computed: {
    // Filter businesses based on search query
    filteredBusinesses() {
      if (!this.searchQuery.trim()) {
        return this.businessesNeedingUpdate;
      }

      const query = this.searchQuery.toLowerCase().trim();
      return this.businessesNeedingUpdate.filter(b => {
        return (
            (b.businessID && b.businessID.toString().toLowerCase().includes(query)) ||
            (b.businessName && b.businessName.toLowerCase().includes(query)) ||
            (b.fullNames && b.fullNames.toLowerCase().includes(query)) ||
            (b.owner && b.owner.toLowerCase().includes(query)) ||
            (b.businessPhone && b.businessPhone.includes(query)) ||
            (b.ownerPhone && b.ownerPhone.includes(query)) ||
            (b.subCountyName && b.subCountyName.toLowerCase().includes(query)) ||
            (b.wardName && b.wardName.toLowerCase().includes(query))
        );
      });
    },

    // Paginated businesses for modal
    paginatedBusinesses() {
      const start = (this.modalCurrentPage - 1) * this.modalPageSize;
      const end = start + parseInt(this.modalPageSize);
      return this.filteredBusinesses.slice(start, end);
    },

    // Total pages for modal
    modalTotalPages() {
      return Math.ceil(this.filteredBusinesses.length / this.modalPageSize) || 1;
    },

    // Get default fiscal year if not provided
    computedFiscalYear() {
      if (this.fiscalYear) {
        return this.fiscalYear;
      }
      const now = new Date();
      const month = now.getMonth() + 1;
      const year = now.getFullYear();
      return month >= 10 ? year + 1 : year;
    },

    // Scan progress percentage
    scanProgressPercent() {
      if (this.scanProgress.total === 0) return 0;
      return Math.round((this.scanProgress.checked / this.scanProgress.total) * 100);
    }
  },

  watch: {
    // Watch for subCounty filter changes - reset scan state
    subCountyName: {
      handler(newVal, oldVal) {
        console.log('subCountyName changed:', oldVal, '->', newVal);
        // Only reset if actually changed (not initial undefined -> value)
        if (oldVal !== undefined) {
          this.resetScanState();
        }
      }
    },

    // Watch for ward filter changes - reset scan state
    wardName: {
      handler(newVal, oldVal) {
        console.log('wardName changed:', oldVal, '->', newVal);
        if (oldVal !== undefined) {
          this.resetScanState();
        }
      }
    },

    // Watch for zone filter changes - reset scan state
    zone: {
      handler(newVal, oldVal) {
        console.log('zone changed:', oldVal, '->', newVal);
        if (oldVal !== undefined) {
          this.resetScanState();
        }
      }
    },

    // Reset modal page when search changes
    searchQuery() {
      this.modalCurrentPage = 1;
    },

    // Reset modal page when page size changes
    modalPageSize() {
      this.modalCurrentPage = 1;
    },

    // Emit scanning state changes to parent
    isScanning: {
      immediate: true,
      handler(newVal) {
        this.$emit('scanning-changed', newVal);
      }
    },

    // Emit paused state changes to parent
    isPaused: {
      immediate: true,
      handler(newVal) {
        this.$emit('paused-changed', newVal);
      }
    }
  },

  mounted() {
    // Initialize Bootstrap modal
    this.$nextTick(() => {
      const modalEl = this.$refs.readinessModal;
      if (modalEl && typeof bootstrap !== 'undefined') {
        this.modalInstance = new bootstrap.Modal(modalEl);
      }
    });
  },

  beforeDestroy() {
    // Clean up timer
    if (this.scanTimer) {
      clearInterval(this.scanTimer);
    }
  },

  methods: {
    // Number formatter
    numberFormat(num) {
      return new Intl.NumberFormat('en-GB').format(num);
    },

    // Reset scan state without auto-starting (when filters change)
    resetScanState() {
      console.log('resetScanState called - filter changed');

      // Cancel any existing scan
      this.cancelCurrentScan();

      // Reset all state
      this.scanCompleted = false;
      this.isPaused = false;
      this.businessesNeedingUpdate = [];
      this.summary = { total: 0, ready: 0, notReady: 0, fiscalYear: '' };
      this.scanProgress = {
        total: 0,
        checked: 0,
        ready: 0,
        notReady: 0,
        currentPage: 1,
        elapsedTime: 0,
        estimatedRemaining: 0,
        startTime: null,
        pausedPage: 1
      };

      // Emit to parent to reset summary
      this.$emit('readiness-checked', {
        summary: this.summary,
        businessesNeedingUpdate: []
      });
    },

    // Cancel any ongoing scan (internal use)
    cancelCurrentScan() {
      // Stop timer
      if (this.scanTimer) {
        clearInterval(this.scanTimer);
        this.scanTimer = null;
      }

      // Set flag to stop the scan loop
      this.isScanning = false;
      this.isPaused = false;
    },

    // Pause scanning (user action)
    pauseScan() {
      console.log('Pausing scan at page', this.scanProgress.currentPage);

      // Stop the timer
      if (this.scanTimer) {
        clearInterval(this.scanTimer);
        this.scanTimer = null;
      }

      // Save where we paused
      this.scanProgress.pausedPage = this.scanProgress.currentPage;

      // Set paused state
      this.isPaused = true;
      this.isScanning = false;
    },

    // Resume scanning from where we paused (user action)
    async resumeScan() {
      console.log('Resuming scan from page', this.scanProgress.pausedPage);

      this.isPaused = false;
      this.isScanning = true;

      // Restart the timer
      const pausedTime = this.scanProgress.elapsedTime;
      this.scanProgress.startTime = Date.now() - (pausedTime * 1000);

      this.scanTimer = setInterval(() => {
        this.scanProgress.elapsedTime = Math.floor((Date.now() - this.scanProgress.startTime) / 1000);

        if (this.scanProgress.checked > 0) {
          const avgTimePerRecord = this.scanProgress.elapsedTime / this.scanProgress.checked;
          const remainingRecords = this.scanProgress.total - this.scanProgress.checked;
          this.scanProgress.estimatedRemaining = Math.floor(avgTimePerRecord * remainingRecords);
        }
      }, 1000);

      // Continue processing from where we left off
      const batchSize = 100;
      const totalPages = Math.ceil(this.scanProgress.total / batchSize);

      for (let page = this.scanProgress.pausedPage; page <= totalPages; page++) {
        // Check if paused or cancelled
        if (!this.isScanning || this.isPaused) {
          console.log('Scan stopped at page', page);
          break;
        }

        this.scanProgress.currentPage = page;

        try {
          await this.scanBatch(page, batchSize);
        } catch (error) {
          console.error('Error scanning batch:', error);
        }

        await this.delay(100);
      }

      // If completed (not paused/cancelled)
      if (this.isScanning && !this.isPaused) {
        this.completeScan();
      }
    },

    // Cancel scanning (user action)
    cancelScan() {
      console.log('Cancelling scan');

      // Stop timer
      if (this.scanTimer) {
        clearInterval(this.scanTimer);
        this.scanTimer = null;
      }

      // Reset to initial state
      this.isScanning = false;
      this.isPaused = false;
      this.scanCompleted = false;
      this.businessesNeedingUpdate = [];
      this.summary = { total: 0, ready: 0, notReady: 0, fiscalYear: '' };
      this.scanProgress = {
        total: 0,
        checked: 0,
        ready: 0,
        notReady: 0,
        currentPage: 1,
        elapsedTime: 0,
        estimatedRemaining: 0,
        startTime: null,
        pausedPage: 1
      };

      // Emit to parent
      this.$emit('readiness-checked', {
        summary: this.summary,
        businessesNeedingUpdate: []
      });
    },

    // Complete the scan (called when scan finishes successfully)
    completeScan() {
      console.log('Completing scan');

      this.isScanning = false;
      this.isPaused = false;
      this.scanCompleted = true;

      // Stop timer
      if (this.scanTimer) {
        clearInterval(this.scanTimer);
        this.scanTimer = null;
      }

      // Update summary
      this.summary = {
        total: this.scanProgress.checked,
        ready: this.scanProgress.ready,
        notReady: this.scanProgress.notReady,
        fiscalYear: this.computedFiscalYear
      };

      console.log('Scan complete:', this.summary);

      // Emit readiness data to parent
      this.$emit('readiness-checked', {
        summary: this.summary,
        businessesNeedingUpdate: this.businessesNeedingUpdate
      });

      // Update parent entries with isReady status
      this.updateParentEntries();
    },

    // Format time in seconds to MM:SS or HH:MM:SS
    formatTime(seconds) {
      if (seconds < 60) {
        return `${seconds}s`;
      } else if (seconds < 3600) {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}m ${secs}s`;
      } else {
        const hours = Math.floor(seconds / 3600);
        const mins = Math.floor((seconds % 3600) / 60);
        return `${hours}h ${mins}m`;
      }
    },

    // Open the modal
    openModal() {
      if (this.modalInstance) {
        this.modalInstance.show();
      } else {
        const modalEl = document.getElementById('businessReadinessModal');
        if (modalEl && typeof bootstrap !== 'undefined') {
          this.modalInstance = new bootstrap.Modal(modalEl);
          this.modalInstance.show();
        }
      }
    },

    // Close the modal
    closeModal() {
      if (this.modalInstance) {
        this.modalInstance.hide();
      }
    },

    // Start full scan of all businesses
    async startFullScan() {
      if (this.isScanning) {
        console.log('Scan already in progress, skipping');
        return;
      }

      console.log('Starting full scan...');

      this.isScanning = true;
      this.isPaused = false;
      this.scanCompleted = false;
      this.businessesNeedingUpdate = [];

      // Initialize scan progress
      this.scanProgress = {
        total: 0,
        checked: 0,
        ready: 0,
        notReady: 0,
        currentPage: 1,
        elapsedTime: 0,
        estimatedRemaining: 0,
        startTime: Date.now(),
        pausedPage: 1
      };

      // Always get fresh total count
      try {
        await this.getTotalCount();
      } catch (e) {
        console.error('Failed to get total count:', e);
        this.isScanning = false;
        return;
      }

      // If no records, complete immediately
      if (this.scanProgress.total === 0) {
        console.log('No records to scan');
        this.isScanning = false;
        this.scanCompleted = true;
        this.summary = { total: 0, ready: 0, notReady: 0, fiscalYear: this.computedFiscalYear };
        this.$emit('readiness-checked', {
          summary: this.summary,
          businessesNeedingUpdate: []
        });
        return;
      }

      // Start elapsed time timer
      this.scanTimer = setInterval(() => {
        this.scanProgress.elapsedTime = Math.floor((Date.now() - this.scanProgress.startTime) / 1000);

        // Calculate estimated remaining time
        if (this.scanProgress.checked > 0) {
          const avgTimePerRecord = this.scanProgress.elapsedTime / this.scanProgress.checked;
          const remainingRecords = this.scanProgress.total - this.scanProgress.checked;
          this.scanProgress.estimatedRemaining = Math.floor(avgTimePerRecord * remainingRecords);
        }
      }, 1000);

      // Process in batches
      const batchSize = 100; // Check 100 records at a time
      const totalPages = Math.ceil(this.scanProgress.total / batchSize);

      console.log(`Scanning ${this.scanProgress.total} records in ${totalPages} batches`);

      for (let page = 1; page <= totalPages; page++) {
        // Check if paused or cancelled
        if (!this.isScanning || this.isPaused) {
          console.log('Scan stopped at page', page);
          break;
        }

        this.scanProgress.currentPage = page;

        try {
          await this.scanBatch(page, batchSize);
        } catch (error) {
          console.error('Error scanning batch:', error);
        }

        // Small delay between batches to prevent overwhelming the server
        await this.delay(100);
      }

      // If completed (not paused/cancelled)
      if (this.isScanning && !this.isPaused) {
        this.completeScan();
      }
    },

    // Get total count of pending records
    async getTotalCount() {
      return new Promise((resolve, reject) => {
        const data = new FormData();
        data.append("function", "getPreviousRegister");
        data.append("page", 1);
        data.append("rows_per_page", 1);
        data.append("keyword", "");
        data.append("subCountyName", this.subCountyName || '');
        data.append("wardName", this.wardName || '');
        data.append("zone", this.zone || '');
        data.append("renewalStatus", "PENDING");

        execute(data, trade)
            .then((res) => {
              if (res.data.success) {
                this.scanProgress.total = parseInt(res.data.data.totalItems?.recordCount) || 0;
                resolve(this.scanProgress.total);
              } else {
                reject(new Error(res.data.message));
              }
            })
            .catch(reject);
      });
    },

    // Scan a batch of records
    async scanBatch(page, batchSize) {
      return new Promise((resolve, reject) => {
        // First, fetch the records for this page
        const fetchData = new FormData();
        fetchData.append("function", "getPreviousRegister");
        fetchData.append("page", page);
        fetchData.append("rows_per_page", batchSize);
        fetchData.append("keyword", "");
        fetchData.append("subCountyName", this.subCountyName || '');
        fetchData.append("wardName", this.wardName || '');
        fetchData.append("zone", this.zone || '');
        fetchData.append("renewalStatus", "PENDING");

        execute(fetchData, trade)
            .then((res) => {
              if (res.data.success) {
                const entries = res.data.data.entries || [];

                if (entries.length === 0) {
                  resolve();
                  return;
                }

                // Get business IDs to check
                const businessIDs = entries.map(e => e.businessID).filter(id => id);

                // Now check readiness for these business IDs
                const checkData = new FormData();
                checkData.append("function", "checkBusinessInvoiceReadiness");
                checkData.append("businessIDs", JSON.stringify(businessIDs));
                checkData.append("fiscalYear", this.computedFiscalYear);

                execute(checkData, trade)
                    .then((checkRes) => {
                      if (checkRes.data.success) {
                        const businessStatus = checkRes.data.data.businesses || [];

                        // Update counts
                        businessStatus.forEach(status => {
                          this.scanProgress.checked++;
                          if (status.isReady) {
                            this.scanProgress.ready++;
                          } else {
                            this.scanProgress.notReady++;
                            // Add to list of businesses needing update
                            this.businessesNeedingUpdate.push(status);
                          }
                        });

                        resolve();
                      } else {
                        // If check fails, count all as checked but unknown
                        this.scanProgress.checked += entries.length;
                        resolve();
                      }
                    })
                    .catch((e) => {
                      console.error('Error checking readiness:', e);
                      this.scanProgress.checked += entries.length;
                      resolve();
                    });
              } else {
                reject(new Error(res.data.message));
              }
            })
            .catch(reject);
      });
    },

    // Update parent entries with isReady status
    updateParentEntries() {
      if (!this.entries || this.entries.length === 0) return;

      // Create a Set of businessIDs that need update for faster lookup
      const notReadySet = new Set(this.businessesNeedingUpdate.map(b => b.businessID));

      // Update each entry in parent (Vue 3 compatible)
      this.entries.forEach(entry => {
        if (entry.businessID) {
          entry.isReady = !notReadySet.has(entry.businessID);
        }
      });
    },

    // Delay helper function
    delay(ms) {
      return new Promise(resolve => setTimeout(resolve, ms));
    },

    // Refresh the list
    refreshList() {
      this.startFullScan();
    },

    // Initiate update for a business - calls same function as "Initiate Verification"
    initiateUpdate(business, index) {
      this.updatingIndex = index;

      // Store the business being updated so we can remove it later
      this.lastUpdatedBusinessID = business.businessID;

      // Close this modal
      this.closeModal();

      // Emit event to parent to handle the update
      // Parent will call getBusinessDetails and open receipting-modal
      this.$emit('initiate-update', {
        id: business.id,
        businessID: business.businessID,
        index: index,
        business: business
      });

      // Reset updating index after a short delay
      setTimeout(() => {
        this.updatingIndex = null;
      }, 1000);
    },

    // Remove a business from the needs-update list (called after successful update)
    removeBusinessFromList(businessID) {
      if (!businessID) return;

      const index = this.businessesNeedingUpdate.findIndex(b => b.businessID === businessID || b.businessID === String(businessID));

      if (index !== -1) {
        // Remove from list
        this.businessesNeedingUpdate.splice(index, 1);

        // Update counts
        this.scanProgress.notReady = Math.max(0, this.scanProgress.notReady - 1);
        this.scanProgress.ready = this.scanProgress.ready + 1;

        this.summary.notReady = Math.max(0, this.summary.notReady - 1);
        this.summary.ready = this.summary.ready + 1;

        console.log(`Removed business ${businessID} from needs-update list. Remaining: ${this.businessesNeedingUpdate.length}`);

        // Emit updated summary to parent
        this.$emit('readiness-checked', {
          summary: this.summary,
          businessesNeedingUpdate: this.businessesNeedingUpdate
        });

        // Update the entry in parent's entries array
        this.updateSingleEntryStatus(businessID, true);
      }
    },

    // Update a single entry's isReady status in parent
    updateSingleEntryStatus(businessID, isReady) {
      if (!this.entries || this.entries.length === 0) return;

      const entry = this.entries.find(e => e.businessID === businessID || e.businessID === String(businessID));
      if (entry) {
        entry.isReady = isReady;
      }
    },

    // Called by parent when receipting modal closes after an update
    onUpdateCompleted(businessID) {
      // Remove from the needs-update list
      this.removeBusinessFromList(businessID || this.lastUpdatedBusinessID);
      this.lastUpdatedBusinessID = null;
    },

    // Get the last updated business ID
    getLastUpdatedBusinessID() {
      return this.lastUpdatedBusinessID;
    },

    // Get summary text
    getSummaryText() {
      return `Ready: ${this.summary.ready} | Not Ready: ${this.summary.notReady} | Total: ${this.summary.total}`;
    },

    // Check if there are unready businesses in a selection
    hasUnreadyInSelection(selectedEntries) {
      if (!selectedEntries || selectedEntries.length === 0) return false;
      return selectedEntries.some(entry => entry.isReady === false);
    },

    // Get count of ready businesses
    getReadyCount() {
      return this.summary.ready || 0;
    },

    // Get count of not ready businesses
    getNotReadyCount() {
      return this.summary.notReady || 0;
    }
  }
};
</script>

<style scoped>
.readiness-alert-container {
  width: 100%;
}

.sticky-top {
  position: sticky;
  top: 0;
  z-index: 1;
}

.table-responsive {
  position: relative;
}

.modal-body {
  position: relative;
}

/* Custom scrollbar for table */
.table-responsive::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.table-responsive::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.table-responsive::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

.table-responsive::-webkit-scrollbar-thumb:hover {
  background: #a1a1a1;
}

/* Hover effect on table rows */
.table-hover tbody tr:hover {
  background-color: rgba(0, 123, 255, 0.05);
}

/* Badge styling */
.badge {
  font-weight: 500;
}

/* Button in table cell */
.table td .btn {
  white-space: nowrap;
}

/* Progress bar animation */
.progress-bar-animated {
  animation: progress-bar-stripes 1s linear infinite;
}
</style>